import type { BankTransaction, Bill } from '@repo/shared-types';
import type { ThreeWayMatch } from '@repo/shared-types/accounting/purchaseOrders';
import type { Distribution } from '@repo/shared-types/accounting/schemas';
import type { ManagementFeeSettlementReport } from '@repo/shared-types/accounting/managementFeeSettlements';
import type { ManagementFeeSweepReport } from '@repo/shared-types/accounting/managementFees';
import type { OwnerDistributionRunReport } from '@repo/shared-types/accounting/ownerDistributions';
import { bankTransactionSchema, billSchema, distributionSchema } from '@repo/shared-types/accounting/schemas';
import { threeWayMatchSchema } from '@repo/shared-types/accounting/purchaseOrderSchemas';
import { managementFeeSettlementReportSchema } from '@repo/shared-types/accounting/managementFeeSettlementSchemas';
import { managementFeeSweepReportSchema } from '@repo/shared-types/accounting/managementFeeSweepSchemas';
import { ownerDistributionRunReportSchema } from '@repo/shared-types/accounting/ownerDistributionSchemas';
import {
  statementBatchCreateResponseSchema,
  statementBatchExecuteResponseSchema,
  type StatementBatchRow,
} from '@repo/shared-types/accounting/statementBatchSchemas';
import {
  bankTransactionImportResponseSchema,
  reconciliationCompleteResponseSchema,
  type BankTransactionImportResponse,
  type ReconciliationCompleteResponse,
} from '@repo/shared-types/accounting/bankReconciliationSchemas';
import type { z } from 'zod';
import { logger, validateEntity } from '@repo/shared-utils';

function throwInvalid(label: string, errors: unknown): never {
  logger.error(`Invalid ${label} mutation response:`, errors);
  throw new Error(`Invalid ${label} data received from API`);
}

export function parseOwnerDistributionRunReport(data: unknown): OwnerDistributionRunReport {
  const validation = validateEntity(ownerDistributionRunReportSchema, data);
  if (!validation.valid) {
    throwInvalid('owner distribution', validation.errors);
  }
  return validation.data!;
}

export function parseManagementFeeSweepReport(data: unknown): ManagementFeeSweepReport {
  const validation = validateEntity(managementFeeSweepReportSchema, data);
  if (!validation.valid) {
    throwInvalid('management fee sweep', validation.errors);
  }
  return validation.data!;
}

export function parseManagementFeeSettlementReport(data: unknown): ManagementFeeSettlementReport {
  const validation = validateEntity(managementFeeSettlementReportSchema, data);
  if (!validation.valid) {
    throwInvalid('management fee settlement', validation.errors);
  }
  return validation.data!;
}

export function parseStatementBatchCreateResponse(data: unknown): { batch: StatementBatchRow } {
  const validation = validateEntity(statementBatchCreateResponseSchema, data);
  if (!validation.valid) {
    throwInvalid('statement batch create', validation.errors);
  }
  return validation.data!;
}

export type StatementBatchExecuteResponse = z.infer<typeof statementBatchExecuteResponseSchema>;

export function parseStatementBatchExecuteResponse(data: unknown): StatementBatchExecuteResponse {
  const validation = validateEntity(statementBatchExecuteResponseSchema, data);
  if (!validation.valid) {
    throwInvalid('statement batch execute', validation.errors);
  }
  return validation.data!;
}

export function parseBankTransactionMutationResponse(data: unknown): BankTransaction {
  const validation = validateEntity(bankTransactionSchema, data);
  if (!validation.valid) {
    throwInvalid('bank transaction', validation.errors);
  }
  return validation.data!;
}

export function parseBankTransactionImportResponse(data: unknown): BankTransactionImportResponse {
  const validation = validateEntity(bankTransactionImportResponseSchema, data);
  if (!validation.valid) {
    throwInvalid('bank transaction import', validation.errors);
  }
  return validation.data!;
}

export function parseReconciliationCompleteResponse(data: unknown): ReconciliationCompleteResponse {
  const validation = validateEntity(reconciliationCompleteResponseSchema, data);
  if (!validation.valid) {
    throwInvalid('reconciliation complete', validation.errors);
  }
  return validation.data!;
}

export function parseThreeWayMatchMutationResponse(data: unknown): ThreeWayMatch {
  const validation = validateEntity(threeWayMatchSchema, data);
  if (!validation.valid) {
    throwInvalid('three-way match', validation.errors);
  }
  return validation.data!;
}

export function parseBillMutationResponse(data: unknown): Bill {
  const validation = validateEntity(billSchema, data);
  if (!validation.valid) {
    throwInvalid('bill', validation.errors);
  }
  return validation.data!;
}

export function parseDistributionMutationResponse(data: unknown): Distribution {
  const validation = validateEntity(distributionSchema, data);
  if (!validation.valid) {
    throwInvalid('distribution', validation.errors);
  }
  return validation.data!;
}
